import React, { FC, useState } from "react";
import { MatterRelationEditor } from "../matter-relation-editor";
import { VpdList } from "../../vpd-list";
import { DiscountRow } from "../../discounts";
import { Discount } from "../../../data-types";
import { DndContext, useSensor, useSensors, PointerSensor, DragOverlay } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableItem, DraggableHandle } from "../../draggable-list";

type Props = {
  discounts: Discount[];
  onUpdate: (discount: Partial<Discount>) => void;
  onDelete: (id: number) => void;
  onCreate: (discount?: Partial<Discount>) => void;
  libraryDiscounts: string[];
  onReorder: (activeId: number, overId: number) => void;
  onAutoFocus: () => void;
  currencySymbol: string;
};
export const MatterDiscountsEditor: FC<Props> = ({
  discounts,
  onCreate,
  onDelete,
  onUpdate,
  libraryDiscounts,
  onReorder,
  onAutoFocus,
  currencySymbol,
}) => {
  const sensors = useSensors(useSensor(PointerSensor));
  const [activeDiscount, setActiveDiscount] = useState<Discount | null>(null);
  return (
    <MatterRelationEditor
      workspaceContent={
        <DndContext
          onDragStart={({ active }) => {
            setActiveDiscount(discounts.find((p) => p.id.toString() === active.id)!);
          }}
          onDragEnd={({ active, over }) =>
            over && onReorder(parseInt(active.id), parseInt(over.id))
          }
          sensors={sensors}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={discounts.map((p) => p.id.toString())}
            strategy={verticalListSortingStrategy}
          >
            <VpdList
              itemKeyProperty="id"
              itemTextProperty="reason"
              loading={false}
              split={false}
              items={discounts}
              renderItem={(item: Discount) => {
                const isAutoFocused = item?.autoFocus ?? false;
                return (
                  <SortableItem id={item.id.toString()} key={item.id}>
                    {({ attributes, listeners }) => (
                      <VpdList.Item
                        key={item.id.toString()}
                        content={
                          <DiscountRow
                            currencySymbol={currencySymbol}
                            autoFocus={isAutoFocused}
                            onAutoFocus={onAutoFocus}
                            discount={item}
                            libraryDiscounts={libraryDiscounts}
                            onUpdate={onUpdate}
                            onDelete={onDelete}
                          />
                        }
                        contentPrefix={<DraggableHandle {...attributes} {...listeners} />}
                      />
                    )}
                  </SortableItem>
                );
              }}
              listStyle="primary"
              labels={{
                emptyText: "No items available.",
                loadingText: "Loading items...",
              }}
            />
          </SortableContext>
          <DragOverlay>
            {activeDiscount ? (
              <VpdList
                itemKeyProperty="id"
                itemTextProperty="reason"
                loading={false}
                split={false}
                items={[activeDiscount]}
                renderItem={(item: Discount) => (
                  <VpdList.Item
                    key="rubbish"
                    content={
                      <DiscountRow
                        currencySymbol={currencySymbol}
                        discount={item}
                        libraryDiscounts={libraryDiscounts}
                        onUpdate={onUpdate}
                        onDelete={onDelete}
                      />
                    }
                    contentPrefix={<DraggableHandle />}
                  />
                )}
                listStyle="primary"
                labels={{
                  emptyText: "No items available.",
                  loadingText: "Loading items...",
                }}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      }
      addNew={() => onCreate()}
      showAddNew={true}
    />
  );
};
